import { marked } from 'marked';
import DOMPurify from 'dompurify';
import JSZip from 'jszip';

/**
 * Shared Markdown tool contract.
 *
 * A format module owns parsing, format-specific options, validation, and the
 * exact conversion boundary. It calls setupMarkdownToolPage once, then uses:
 *
 *   page.runConversion(async ({ signal, report, isCurrent }) => ({
 *     markdown: '...',
 *     warnings: ['...'],
 *     assets: [{ path: 'assets/image.png', blob }],
 *   }));
 *
 * `report(message, progress)` accepts a user-facing status and an optional
 * number from 0 to 1. A converter must observe `signal.aborted` and stop its
 * own worker or request when cancellation is signalled. `isCurrent()` guards
 * against stale asynchronous results. The shared layer never branches on a
 * file format.
 */

const MARKDOWN_TAGS = [
  'a', 'blockquote', 'br', 'code', 'del', 'em', 'h1', 'h2', 'h3', 'h4',
  'h5', 'h6', 'hr', 'li', 'ol', 'p', 'pre', 'strong', 'table', 'tbody',
  'td', 'tfoot', 'th', 'thead', 'tr', 'ul',
];
const MARKDOWN_ATTRIBUTES = ['colspan', 'href', 'rel', 'rowspan', 'target', 'title'];
const UNSAFE_HREF = /^(?:javascript|data|vbscript):/i;
const EXTERNAL_HREF = /^(?:https?|mailto):/i;
const MAX_MARKDOWN_CHARS = 8 * 1024 * 1024;
const MAX_ASSETS = 256;
const MAX_ASSET_BYTES = 32 * 1024 * 1024;
const MAX_TOTAL_ASSET_BYTES = 64 * 1024 * 1024;

function asMessage(error, fallback = 'The conversion could not be completed.') {
  return error instanceof Error && error.message ? error.message : fallback;
}

function clampProgress(progress) {
  if (progress === null || progress === undefined || progress === '') return null;
  const value = Number(progress);
  if (!Number.isFinite(value)) return null;
  return Math.min(1, Math.max(0, value));
}

export function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function sanitizeFileName(name, fallback = 'document') {
  const base = String(name || '').replace(/[^a-z0-9._-]+/gi, '-').replace(/^-+|-+$/g, '');
  return base || fallback;
}

export function normalizeAssetPath(path) {
  const normalized = String(path || '').replaceAll('\\', '/').replace(/^\/+/, '');
  if (!normalized.startsWith('assets/') || normalized.length <= 'assets/'.length || normalized.split('/').some((part) => !part || part === '.' || part === '..')) {
    throw new Error(`Invalid asset path: ${path}`);
  }
  return normalized;
}

function isAssetBody(value) {
  return value instanceof Blob || value instanceof Uint8Array || value instanceof ArrayBuffer || typeof value === 'string';
}

function assetBodySize(value) {
  if (value instanceof Blob) return value.size;
  if (value instanceof Uint8Array) return value.byteLength;
  if (value instanceof ArrayBuffer) return value.byteLength;
  return new TextEncoder().encode(value).byteLength;
}

export function normalizeResult(result) {
  if (!result || typeof result.markdown !== 'string') {
    throw new TypeError('A conversion result must contain a Markdown string.');
  }
  if (result.markdown.length > MAX_MARKDOWN_CHARS) {
    throw new RangeError('The Markdown result is larger than 8 MB.');
  }
  const warnings = Array.isArray(result.warnings)
    ? result.warnings.map((warning) => String(warning)).filter(Boolean)
    : [];
  const assets = Array.isArray(result.assets)
    ? result.assets.map((asset) => {
      if (!asset || !isAssetBody(asset.blob)) throw new TypeError('Every result asset needs a Blob or byte body.');
      return {
        path: normalizeAssetPath(asset.path),
        blob: asset.blob,
        contentType: asset.contentType ? String(asset.contentType) : '',
      };
    })
    : [];
  if (assets.length > MAX_ASSETS) throw new RangeError('The conversion returned too many assets.');
  const paths = new Set();
  let totalBytes = 0;
  assets.forEach((asset) => {
    if (paths.has(asset.path)) throw new TypeError(`Duplicate asset path: ${asset.path}`);
    paths.add(asset.path);
    const size = assetBodySize(asset.blob);
    if (size > MAX_ASSET_BYTES) throw new RangeError(`Asset ${asset.path} is larger than 32 MB.`);
    totalBytes += size;
  });
  if (totalBytes > MAX_TOTAL_ASSET_BYTES) throw new RangeError('The conversion assets are larger than 64 MB in total.');
  return { markdown: result.markdown, warnings, assets };
}

export function setStatus(node, message, kind = '') {
  if (!node) return;
  node.textContent = message;
  node.className = `status-message${kind ? ` ${kind}` : ''}`;
}

export function wireDropZone({ dropZone, fileInput, onFiles, getDisabled = () => false }) {
  if (!dropZone || !fileInput) throw new Error('A drop zone needs both a container and file input.');
  const choose = () => { if (!getDisabled()) fileInput.click(); };
  const setDragging = (value) => dropZone.classList.toggle('is-dragging', value);
  dropZone.addEventListener('click', (event) => {
    if (event.target !== fileInput) choose();
  });
  dropZone.addEventListener('keydown', (event) => {
    if ((event.key === 'Enter' || event.key === ' ') && !getDisabled()) {
      event.preventDefault();
      choose();
    }
  });
  dropZone.addEventListener('dragover', (event) => {
    if (getDisabled()) return;
    event.preventDefault();
    setDragging(true);
  });
  dropZone.addEventListener('dragleave', () => setDragging(false));
  dropZone.addEventListener('drop', (event) => {
    event.preventDefault();
    setDragging(false);
    if (!getDisabled()) onFiles(event.dataTransfer?.files || []);
  });
  fileInput.addEventListener('change', () => {
    const files = fileInput.files || [];
    onFiles(files);
    fileInput.value = '';
  });
  return {
    choose,
    setDisabled(disabled) {
      fileInput.disabled = Boolean(disabled);
      dropZone.setAttribute('aria-disabled', String(Boolean(disabled)));
      if (disabled) setDragging(false);
    },
  };
}

export function createRunGate({ onProgress = () => {}, onCancel = () => {} } = {}) {
  let active = null;
  let nextId = 0;

  function cancel(reason = 'cancelled') {
    if (!active) return;
    const run = active;
    active = null;
    run.controller.abort(reason);
    onCancel(run, reason);
  }

  return {
    start() {
      cancel('replaced');
      const controller = new AbortController();
      const run = {
        id: ++nextId,
        controller,
        signal: controller.signal,
        isCurrent: () => active === run && !controller.signal.aborted,
        report(message, progress = null) {
          if (run.isCurrent()) onProgress({ message: String(message || ''), progress: clampProgress(progress), run });
        },
        cancel: () => cancel('cancelled'),
      };
      active = run;
      return run;
    },
    cancel,
    finish(run) {
      if (active === run) active = null;
    },
    get active() {
      return active;
    },
  };
}

function safeHref(value) {
  const href = String(value || '').trim();
  if (!href || UNSAFE_HREF.test(href) || href.startsWith('//')) return '';
  if (EXTERNAL_HREF.test(href)) {
    try {
      const parsed = new URL(href, document.baseURI);
      if ((parsed.protocol === 'http:' || parsed.protocol === 'https:') && !parsed.hostname) return '';
      return href;
    } catch {
      return '';
    }
  }
  if (/^[a-z][a-z\d+.-]*:/i.test(href)) return '';
  return href;
}

export function renderMarkdownPreview(node, markdown) {
  if (!node) return '';
  try {
    const html = marked.parse(String(markdown || ''), { gfm: true, breaks: false, async: false });
    const clean = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: MARKDOWN_TAGS,
      ALLOWED_ATTR: MARKDOWN_ATTRIBUTES,
      ALLOW_DATA_ATTR: false,
      FORBID_TAGS: ['audio', 'button', 'embed', 'form', 'iframe', 'img', 'input', 'link', 'meta', 'object', 'script', 'source', 'style', 'video'],
      FORBID_ATTR: ['style'],
    });
    node.innerHTML = clean;
    node.querySelectorAll('a').forEach((link) => {
      const href = safeHref(link.getAttribute('href'));
      if (!href) {
        link.removeAttribute('href');
        link.removeAttribute('target');
        link.removeAttribute('rel');
        return;
      }
      link.setAttribute('href', href);
      link.setAttribute('rel', 'noopener noreferrer nofollow');
      if (EXTERNAL_HREF.test(href)) link.setAttribute('target', '_blank');
    });
    return clean;
  } catch (error) {
    node.replaceChildren();
    node.textContent = `Preview unavailable: ${asMessage(error)}`;
    return '';
  }
}

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function downloadAssets(result, baseName) {
  const zip = new JSZip();
  zip.file('document.md', result.markdown);
  result.assets.forEach((asset) => zip.file(asset.path, asset.blob));
  const blob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
  downloadBlob(blob, `${sanitizeFileName(baseName)}-markdown.zip`);
}

export function createResultWorkspace({
  panel,
  resultText,
  preview,
  resultMeta,
  warningList,
  emptyWarningMessage,
  copyButton,
  downloadButton,
  zipButton,
  getFileName = () => 'document',
  setStatusMessage = () => {},
}) {
  let current = { markdown: '', warnings: [], assets: [] };
  let revision = 0;

  function renderWarnings() {
    if (warningList) {
      warningList.replaceChildren();
      warningList.hidden = current.warnings.length === 0;
    }
    if (emptyWarningMessage) emptyWarningMessage.hidden = current.warnings.length !== 0;
    if (!warningList) return;
    current.warnings.forEach((warning) => {
      const item = document.createElement('li');
      item.textContent = warning;
      warningList.append(item);
    });
  }

  function render() {
    if (resultText && resultText.value !== current.markdown) resultText.value = current.markdown;
    renderMarkdownPreview(preview, current.markdown);
    renderMeta();
    if (panel) panel.hidden = !current.markdown && !current.warnings.length && !current.assets.length;
    if (copyButton) copyButton.disabled = !current.markdown;
    if (downloadButton) downloadButton.disabled = !current.markdown;
    if (zipButton) {
      zipButton.hidden = current.assets.length === 0;
      zipButton.disabled = current.assets.length === 0;
    }
    renderWarnings();
  }

  function renderMeta() {
    if (resultMeta) {
      const parts = [`${current.markdown.length.toLocaleString()} characters`];
      if (current.warnings.length) parts.push(`${current.warnings.length} warning${current.warnings.length === 1 ? '' : 's'}`);
      if (current.assets.length) parts.push(`${current.assets.length} asset${current.assets.length === 1 ? '' : 's'}`);
      resultMeta.textContent = parts.join(' · ');
    }
  }

  function setResult(result) {
    current = normalizeResult(result);
    revision += 1;
    render();
    return current;
  }

  function clear() {
    current = { markdown: '', warnings: [], assets: [] };
    revision += 1;
    render();
  }

  resultText?.addEventListener('input', () => {
    current = { ...current, markdown: resultText.value };
    revision += 1;
    renderMarkdownPreview(preview, current.markdown);
    renderMeta();
  });
  copyButton?.addEventListener('click', async () => {
    if (!current.markdown) return;
    if (!navigator.clipboard?.writeText) {
      resultText?.focus();
      resultText?.select();
      setStatusMessage('Clipboard access needs HTTPS. The Markdown is selected; copy it manually.', 'error');
      return;
    }
    try {
      await navigator.clipboard.writeText(current.markdown);
      setStatusMessage('Markdown copied to the clipboard.', 'success');
    } catch {
      resultText?.focus();
      resultText?.select();
      setStatusMessage('Clipboard access was blocked. The Markdown is selected; copy it manually.', 'error');
    }
  });
  downloadButton?.addEventListener('click', () => {
    if (!current.markdown) return;
    downloadBlob(new Blob([current.markdown], { type: 'text/markdown;charset=utf-8' }), `${sanitizeFileName(getFileName())}.md`);
    setStatusMessage('Markdown download prepared.', 'success');
  });
  zipButton?.addEventListener('click', async () => {
    if (!current.assets.length) return;
    const started = revision;
    try {
      await downloadAssets(current, getFileName());
      if (started === revision) setStatusMessage('Markdown and assets ZIP prepared.', 'success');
    } catch (error) {
      setStatusMessage(asMessage(error, 'The Markdown ZIP could not be created.'), 'error');
    }
  });

  clear();
  return {
    setResult,
    clear,
    getResult: () => current,
    render,
  };
}

export function setupMarkdownToolPage({
  root = document,
  onFiles = () => {},
  onCancel = () => {},
  defaultFileName = 'document',
} = {}) {
  const elements = {
    root,
    fileInput: root.querySelector('#file-input'),
    dropZone: root.querySelector('#drop-zone'),
    formatControls: root.querySelector('#format-controls'),
    processingBoundary: root.querySelector('#processing-boundary'),
    convertButton: root.querySelector('#convert-button'),
    clearButton: root.querySelector('#clear-button'),
    cancelButton: root.querySelector('#cancel-button'),
    status: root.querySelector('#status-message'),
    progress: root.querySelector('#progress-indicator'),
    resultPanel: root.querySelector('#result-panel'),
    resultText: root.querySelector('#result-text'),
    preview: root.querySelector('#markdown-preview'),
    resultMeta: root.querySelector('#result-meta'),
    warningList: root.querySelector('#warning-list'),
    emptyWarningMessage: root.querySelector('#no-warning-message'),
    copyButton: root.querySelector('#copy-button'),
    downloadButton: root.querySelector('#download-button'),
    zipButton: root.querySelector('#zip-button'),
  };
  const setStatusMessage = (message, kind = '') => setStatus(elements.status, message, kind);
  const gate = createRunGate({
    onProgress: ({ message, progress }) => {
      if (message) setStatusMessage(message);
      if (elements.progress) {
        elements.progress.hidden = progress === null;
        if (progress !== null) elements.progress.value = progress;
      }
    },
    onCancel: (run, reason) => onCancel(run, reason),
  });
  const upload = wireDropZone({
    dropZone: elements.dropZone,
    fileInput: elements.fileInput,
    onFiles,
    getDisabled: () => Boolean(gate.active),
  });
  const result = createResultWorkspace({
    panel: elements.resultPanel,
    resultText: elements.resultText,
    preview: elements.preview,
    resultMeta: elements.resultMeta,
    warningList: elements.warningList,
    emptyWarningMessage: elements.emptyWarningMessage,
    copyButton: elements.copyButton,
    downloadButton: elements.downloadButton,
    zipButton: elements.zipButton,
    getFileName: () => defaultFileName,
    setStatusMessage,
  });

  let busy = false;
  let ready = false;

  function setReady(value) {
    ready = Boolean(value);
    if (busy) return;
    if (elements.convertButton) elements.convertButton.disabled = !ready;
    if (elements.clearButton) elements.clearButton.disabled = !ready;
  }

  function setBusy(value) {
    busy = Boolean(value);
    upload.setDisabled(busy);
    if (elements.convertButton) elements.convertButton.disabled = busy || !ready;
    if (elements.clearButton) elements.clearButton.disabled = busy || !ready;
    if (elements.cancelButton) elements.cancelButton.hidden = !busy;
    if (!busy && elements.progress) elements.progress.hidden = true;
    if (elements.formatControls) elements.formatControls.querySelectorAll('input, select, textarea, button').forEach((node) => { node.disabled = Boolean(busy); });
  }

  elements.cancelButton?.addEventListener('click', () => {
    gate.cancel('user');
    setBusy(false);
    setStatusMessage('Conversion canceled. Your source file remains selected.');
  });

  async function runConversion(convert) {
    const run = gate.start();
    setBusy(true);
    try {
      const output = await convert({
        signal: run.signal,
        report: run.report,
        isCurrent: run.isCurrent,
      });
      if (!run.isCurrent()) return null;
      result.setResult(output);
      return output;
    } catch (error) {
      if (run.isCurrent()) setStatusMessage(asMessage(error), 'error');
      throw error;
    } finally {
      const ownsUi = run.isCurrent();
      gate.finish(run);
      if (ownsUi) setBusy(false);
    }
  }

  return { elements, upload, gate, result, setStatus: setStatusMessage, setReady, setBusy, runConversion };
}

export async function readFileBytes(file, maxBytes, label = 'file') {
  if (!file || file.size === 0) throw new Error(`The ${label} is empty.`);
  if (file.size > maxBytes) throw new Error(`The ${label} is larger than ${formatBytes(maxBytes)}.`);
  return new Uint8Array(await file.arrayBuffer());
}

export async function readFileText(file, maxBytes, label = 'file') {
  const bytes = await readFileBytes(file, maxBytes, label);
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
}
